import { useState, useCallback } from "react";

interface UseMutationOptions<TArgs, TResult = any> {
  mutationFn: (args: TArgs) => Promise<TResult>;
  onSuccess?: (result: TResult, args: TArgs) => void | Promise<void>;
  onError?: (error: unknown, args: TArgs) => void;
}

const useMutation = <TArgs, TResult = any>({
  mutationFn,
  onSuccess,
  onError,
}: UseMutationOptions<TArgs, TResult>) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const mutate = useCallback(
    async (args: TArgs) => {
      setLoading(true);
      setError(null);

      try {
        const result = await mutationFn(args);
        await onSuccess?.(result, args);
        return result;
      } catch (err) {
        setError(err);
        onError?.(err, args);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [mutationFn, onSuccess, onError]
  );

  return { mutate, loading, error };
};

export default useMutation;